import { TypedDocumentNode, gql } from '@apollo/client';
import { faDash } from '@fortawesome/pro-solid-svg-icons';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Cell } from 'components/benchObject/BenchObjectTable/ui';

import type { ProjectionCell_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: ProjectionCell_anyPlayer;
  so5FixtureEvent: So5FixtureEvent;
};

const StyledLabelS = styled(LabelS)`
  @media ${laptopAndAbove} {
    font: var(--t-label-m);
    font-weight: bold;
  }
`;

export const ProjectionCell = ({ anyPlayer, so5FixtureEvent }: Props) => {
  const { up: laptopOrAbove } = useScreenSize('laptop');
  const projectedScore = (() => {
    switch (so5FixtureEvent) {
      case So5FixtureEvent.DAILY:
        return anyPlayer.nextDailyFixtureProjectedScore;

      case So5FixtureEvent.CLASSIC:
      default:
        return anyPlayer.nextClassicFixtureProjectedScore;
    }
  })();

  return (
    <Cell>
      {projectedScore === null ? (
        <FontAwesomeIcon icon={faDash} color="var(--c-nd-500)" />
      ) : (
        <Cost
          size={laptopOrAbove ? 4 : 3}
          color="var(--c-rotowire)"
          score={
            <Vertical gap={0} center>
              <StyledLabelS bold color="var(--c-white)">
                <FormattedNumber
                  value={projectedScore}
                  maximumFractionDigits={0}
                />
              </StyledLabelS>
            </Vertical>
          }
          inverted={so5FixtureEvent === So5FixtureEvent.DAILY}
        />
      )}
    </Cell>
  );
};

ProjectionCell.fragments = {
  anyPlayer: gql`
    fragment ProjectionCell_anyPlayer on AnyPlayerInterface {
      slug
      nextDailyFixtureProjectedScore
      nextClassicFixtureProjectedScore
    }
  ` as TypedDocumentNode<ProjectionCell_anyPlayer>,
};
